import { PiqueLogo } from '@pique/component';
import { Divider } from '@pique/component/ui/divider';
import { MenuItem } from '@pique/component/ui/menu';
import { authAtom } from '@pique/core/atoms';
import { AuthService } from '@pique/core/modules/cloud';
import { useI18n } from '@pique/i18n';
import {
  useLiveData,
  useService,
  type WorkspaceMetadata,
  WorkspacesService,
} from '@toeverything/infra';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { AddWorkspace } from './add-workspace';
import * as styles from './index.css';
import { UserAccountItem } from './user-account';
import { AFFiNEWorkspaceList } from './workspace-list';

export const SignInItem = () => {
  const setOpen = useSetAtom(authAtom);

  const t = useI18n();

  const onClickSignIn = useCallback(() => {
    setOpen(state => ({
      ...state,
      openModal: true,
    }));
  }, [setOpen]);

  return (
    <MenuItem
      className={styles.menuItem}
      onClick={onClickSignIn}
      data-testid="cloud-signin-button"
    >
      <div className={styles.signInWrapper}>
        <div className={styles.iconContainer}>
          <PiqueLogo />
        </div>

        <div className={styles.signInTextContainer}>
          <div className={styles.signInTextPrimary}>
            {t['com.pique.workspace.cloud.auth']()}
          </div>
          <div className={styles.signInTextSecondary}>
            {t['com.pique.workspace.cloud.description']()}
          </div>
        </div>
      </div>
    </MenuItem>
  );
};

interface UserWithWorkspaceListProps {
  onEventEnd?: () => void;
  onClickWorkspace?: (workspace: WorkspaceMetadata) => void;
  showSettingsButton?: boolean;
  showEnableCloudButton?: boolean;
}

const UserWithWorkspaceListInner = ({
  onEventEnd,
  onClickWorkspace,
  showSettingsButton,
  showEnableCloudButton,
}: UserWithWorkspaceListProps) => {
  // removed create workspace dialog
  const session = useLiveData(useService(AuthService).session.session$);

  const isAuthenticated = session.status === 'authenticated';

  const setOpenSignIn = useSetAtom(authAtom);

  const openSignInModal = useCallback(() => {
    setOpenSignIn(state => ({
      ...state,
      openModal: true,
    }));
  }, [setOpenSignIn]);

  const onNewWorkspace = useCallback(() => {
    if (!isAuthenticated && !runtimeConfig.allowLocalWorkspace) {
      return openSignInModal();
    }
    onEventEnd?.();
  }, [isAuthenticated, onEventEnd, openSignInModal]);

  const onAddWorkspace = useCallback(() => {
    onEventEnd?.();
  }, [onEventEnd]);

  const workspaceManager = useService(WorkspacesService);
  const workspaces = useLiveData(workspaceManager.list.workspaces$);

  return (
    <div className={styles.workspaceListWrapper}>
      {isAuthenticated ? (
        <UserAccountItem
          email={session.session.account.email ?? 'Unknown User'}
          onEventEnd={onEventEnd}
        />
      ) : (
        <SignInItem />
      )}
      <Divider size="thinner" />
      <AFFiNEWorkspaceList
        onEventEnd={onEventEnd}
        onClickWorkspace={onClickWorkspace}
        showEnableCloudButton={showEnableCloudButton}
        showSettingsButton={showSettingsButton}
      />
      {workspaces.length > 0 ? <Divider size="thinner" /> : null}
      <AddWorkspace
        onAddWorkspace={onAddWorkspace}
        onNewWorkspace={onNewWorkspace}
      />
    </div>
  );
};

export const UserWithWorkspaceList = (props: UserWithWorkspaceListProps) => {
  return <UserWithWorkspaceListInner {...props} />;
};
